import { RotateCcw } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
export default function ProductsFiltersSection({
  query,
  onQueryChange,
  selectedBrand,
  onBrandChange,
  onReset,
  hasActiveTypeFilter = false,
  brandOptions = [],
  visibleStart,
  visibleEnd,
  totalCount,
}) {
  const { t } = useI18n();
  const allBrandsValue = '';
  const visibleLabel =
    totalCount === 0 || visibleStart === visibleEnd ? String(visibleEnd || visibleStart || 0) : `${visibleStart}-${visibleEnd}`;

  const hasQuery = String(query || '').trim() !== '';
  const hasBrand = String(selectedBrand || '') !== allBrandsValue;
  const hasActiveFilters = hasQuery || hasBrand || hasActiveTypeFilter;

  const handleReset = () => {
    if (typeof onReset === 'function') {
      onReset();
      return;
    }
    // Fallback: clear only what this section controls. The host page can
    // pass `onReset` to clear every URL-synced field at once.
    if (hasQuery) onQueryChange('');
    if (hasBrand) onBrandChange(allBrandsValue);
  };

  return (
    <section className="relative overflow-hidden bg-transparent pt-0 pb-10">

      <div className="container relative z-10 mx-auto px-6 lg:px-10">
        <div className="relative z-20 rounded-3xl border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.075),rgba(255,255,255,0.018))] p-px backdrop-blur-sm">
          <div className="relative rounded-[1.68rem] bg-[rgba(15,15,20,0.94)] p-6 sm:p-6">
          <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-2xl font-light uppercase tracking-tight text-white">
                {t('productsPage.filters.title')}
              </h2>
              <p className="text-sm text-white/70">
                {t('productsPage.filters.showing')} {visibleLabel} {t('productsPage.filters.of')} {totalCount}{' '}
                {t('productsPage.filters.products')}
              </p>
            </div>
            <div className="flex-1 space-y-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                <label className="flex flex-1 flex-col justify-center gap-2 text-xs font-[500] uppercase text-white/60">
                  <div className="relative">
                    <input
                      placeholder={t('productsPage.filters.searchPlaceholder', 'Search machines...')}
                      value={query}
                      onChange={(event) => onQueryChange(event.target.value)}
                      className="w-full rounded-2xl border border-white/18 bg-white/8 px-8 py-3 text-sm text-white placeholder:text-white/35 outline-none transition-all duration-300 focus:border-[var(--dash-main)] focus:bg-white/12 focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(2,6,23,0.94)]"
                    />
                  </div>
                </label>
                {hasActiveFilters ? (
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-white/15 bg-white/[0.02] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70 transition-all duration-300 hover:border-[var(--dash-main)] hover:text-white sm:self-auto"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('common.reset', 'Reset')}
                  </button>
                ) : null}
              </div>
              {brandOptions.length > 1 ? (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <p className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">
                    {t('productsPage.filters.brands', 'Brands')}
                  </p>
                  <div className="flex flex-wrap gap-2.5">
                    {brandOptions.map((brand) => {
                      const isActive = selectedBrand === brand;
                      const label =
                        brand === allBrandsValue ? t('productsPage.filters.allBrands', 'All brands') : brand;
                      return (
                        <button
                          key={brand || '__all_brands__'}
                          type="button"
                          onClick={() => onBrandChange(brand)}
                          className={`inline-flex items-center rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] transition-all duration-300 ${
                            isActive
                              ? 'border-[var(--dash-main)] bg-[var(--dash-main)] text-white'
                              : 'border-white/15 bg-white/[0.02] text-white/70 hover:border-[var(--dash-main)] hover:text-white'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
}
